import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../../../../../store/store';
import { clearSession } from '../../../../../../store/AuthSlice';
import { useNavigate } from 'react-router-dom';
import styles from './ReviewsPanel.module.scss';

type ReviewStatus = 'pending' | 'published' | 'rejected' | 'deleted';
type StatusTone = 'ok' | 'info' | 'warn' | 'muted';

type MyReviewItem = {
    id: string;
    productName?: string;
    productSlug?: string;
    productImage?: string;
    rating: number;
    text: string;
    createdAt: string;
    status?: ReviewStatus;
};

const BASE_URL = String(process.env.REACT_APP_SERVER_URL || '').trim();
const PAGE_SIZE = 10;

const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));
const normalizeSpaces = (s: string) =>
    String(s || '')
        .replace(/\s+/g, ' ')
        .trim();
const cut = (s: string, max: number) => (s.length > max ? s.slice(0, max) : s);
const safeNum = (n: unknown, fb = 0) => (typeof n === 'number' && Number.isFinite(n) ? n : fb);

const normalizeStatus = (s: unknown): ReviewStatus => {
    const v = String(s ?? '')
        .trim()
        .toLowerCase();
    if (v === 'pending' || v === 'published' || v === 'rejected' || v === 'deleted') return v;
    return 'pending';
};

const fmtDateRu = (iso: string) => {
    try {
        const t = Date.parse(iso);
        if (!Number.isFinite(t)) return iso;
        return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(t));
    } catch {
        return iso;
    }
};

const ratingLabel = (r: number) => `${clamp(Math.round(r || 0), 1, 5)} из 5`;

const statusMeta = (st: ReviewStatus): { text: string; tone: StatusTone } => {
    if (st === 'published') return { text: 'Опубликован', tone: 'ok' };
    if (st === 'pending') return { text: 'На модерации', tone: 'info' };
    if (st === 'rejected') return { text: 'Отклонён', tone: 'warn' };
    return { text: 'Удалён', tone: 'muted' };
};

function pickArrayPayload(data: any): any[] {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.reviews)) return data.reviews;
    if (Array.isArray(data?.data)) return data.data;
    return [];
}

function uniqueById(items: MyReviewItem[]): MyReviewItem[] {
    const seen = new Set<string>();
    return items.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
    });
}

function sanitizeMyReview(raw: any): MyReviewItem | null {
    if (!raw || typeof raw !== 'object') return null;
    const id = String(raw.id ?? raw._id ?? '').trim();
    if (!id) return null;

    const ratingRaw = Number(raw.rating);
    const rating = clamp(Number.isFinite(ratingRaw) ? ratingRaw : 5, 1, 5);
    const textRaw = cut(normalizeSpaces(String(raw.text ?? '')), 3000);
    const prosRaw = cut(normalizeSpaces(String(raw.pros ?? '')), 600);
    const consRaw = cut(normalizeSpaces(String(raw.cons ?? '')), 600);
    const text = textRaw || [prosRaw ? `Плюсы: ${prosRaw}` : '', consRaw ? `Минусы: ${consRaw}` : ''].filter(Boolean).join(' • ');
    if (!text) return null;

    const createdAtRaw = String(raw.createdAt ?? raw.created_at ?? '').trim();
    const createdAtMs = Date.parse(createdAtRaw);
    const createdAt = Number.isFinite(createdAtMs) ? new Date(createdAtMs).toISOString() : new Date().toISOString();

    const productName = raw.productName ? cut(normalizeSpaces(String(raw.productName)), 180) : undefined;
    const productSlug = raw.productSlug ? String(raw.productSlug).trim() : undefined;
    const productImageId = raw.productImageId ? String(raw.productImageId).trim() : '';
    const productImage = productImageId ? `${BASE_URL}/products/images/${encodeURIComponent(productImageId)}` : undefined;

    return {
        id,
        productName,
        productSlug,
        productImage,
        rating,
        text,
        createdAt,
        status: normalizeStatus(raw.status),
    };
}

function buildProductHref(review: MyReviewItem): string | null {
    const slug = String(review.productSlug || '').trim();
    if (!slug) return null;
    return `/${encodeURIComponent(slug)}#reviews`;
}

const ReviewStars: FC<{ value: number }> = ({ value }) => {
    const v = clamp(Math.round(value || 0), 1, 5);
    return (
        <div className={styles.stars} aria-label={ratingLabel(v)} role="img">
            {Array.from({ length: 5 }).map((_, i) => (
                <img key={i} className={styles.starIcon} src={i < v ? '/icons/new/star_active.svg' : '/icons/new/star.svg'} alt="" aria-hidden="true" />
            ))}
        </div>
    );
};

const ReviewsPanel: FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const abortRef = useRef<AbortController | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const loadingRef = useRef(false);

    const [loadingInitial, setLoadingInitial] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [loadMoreError, setLoadMoreError] = useState('');
    const [items, setItems] = useState<MyReviewItem[]>([]);
    const [total, setTotal] = useState(0);
    const [nextPage, setNextPage] = useState(1);

    const fetchPage = useCallback(
        async (pageToLoad: number, append: boolean) => {
            if (!BASE_URL) {
                setError('REACT_APP_SERVER_URL не задан');
                setItems([]);
                setTotal(0);
                setNextPage(0);
                return;
            }

            if (append && loadingRef.current) return;
            loadingRef.current = true;

            if (append) {
                setLoadingMore(true);
                setLoadMoreError('');
            } else {
                setLoadingInitial(true);
                setError('');
                setLoadMoreError('');
            }

            abortRef.current?.abort();
            const ac = new AbortController();
            abortRef.current = ac;

            try {
                const resp = await fetch(`${BASE_URL}/reviews/my?page=${pageToLoad}&limit=${PAGE_SIZE}&sort=new`, {
                    method: 'GET',
                    credentials: 'include',
                    signal: ac.signal,
                });

                if (resp.status === 401) {
                    dispatch(clearSession());
                    if (append) {
                        setLoadMoreError('Нужна авторизация');
                    } else {
                        setItems([]);
                        setError('Нужна авторизация');
                        setTotal(0);
                        setNextPage(0);
                    }
                    return;
                }

                if (!resp.ok) {
                    const j = await resp.json().catch(() => null);
                    const msg = (j?.message ? String(j.message) : '') || `Ошибка загрузки (${resp.status})`;
                    if (append) {
                        setLoadMoreError(msg);
                    } else {
                        setItems([]);
                        setError(msg);
                        setTotal(0);
                        setNextPage(0);
                    }
                    return;
                }

                const data = await resp.json().catch(() => null);
                const rawArr = pickArrayPayload(data);
                const normalized = rawArr.map(sanitizeMyReview).filter(Boolean) as MyReviewItem[];
                const totalFromApi = typeof data?.total === 'number' && Number.isFinite(data.total) ? data.total : normalized.length;

                setItems((prev) => (append ? uniqueById([...prev, ...normalized]) : normalized));
                setTotal(totalFromApi);
                setNextPage(totalFromApi > pageToLoad * PAGE_SIZE ? pageToLoad + 1 : 0);
            } catch (e: any) {
                if (e?.name === 'AbortError') return;
                const msg = 'Ошибка сети при загрузке отзывов';
                if (append) {
                    setLoadMoreError(msg);
                } else {
                    setItems([]);
                    setError(msg);
                    setTotal(0);
                    setNextPage(0);
                }
            } finally {
                loadingRef.current = false;
                setLoadingInitial(false);
                setLoadingMore(false);
            }
        },
        [dispatch],
    );

    useEffect(() => {
        fetchPage(1, false);
        return () => {
            abortRef.current?.abort();
            observerRef.current?.disconnect();
        };
    }, [fetchPage]);

    useEffect(() => {
        if (!nextPage || !items.length || loadingInitial || loadingMore || loadMoreError) return;
        const node = sentinelRef.current;
        if (!node) return;

        observerRef.current?.disconnect();
        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;
                if (loadingRef.current) return;
                fetchPage(nextPage, true);
            },
            { root: null, rootMargin: '220px 0px', threshold: 0.01 },
        );

        observerRef.current.observe(node);

        return () => {
            observerRef.current?.disconnect();
        };
    }, [fetchPage, items.length, loadMoreError, loadingInitial, loadingMore, nextPage]);

    const reviewsCount = safeNum(total, items.length);

    return (
        <div className={styles.wrapper} aria-label="Мои отзывы">
            <div className={styles.head}>
                <h2 className={styles.title}>Мои отзывы</h2>
                {reviewsCount > 0 && <span className={styles.count}>{reviewsCount} шт</span>}
            </div>

            {loadingInitial ? (
                <div className={styles.state} aria-live="polite" aria-busy="true">
                    Загружаем ваши отзывы…
                </div>
            ) : error ? (
                <div className={`${styles.state} ${styles.stateError}`} role="alert" aria-live="polite">
                    <div className={styles.stateTitle}>Не удалось загрузить отзывы</div>
                    <div className={styles.stateText}>{error}</div>
                </div>
            ) : items.length === 0 ? (
                <div className={styles.state} aria-live="polite">
                    <div className={styles.stateTitle}>Пока нет отзывов</div>
                    <div className={styles.stateText}>Здесь появятся ваши отзывы после модерации и публикации.</div>
                </div>
            ) : (
                <>
                    <div className={styles.list} role="list">
                        {items.map((review) => {
                            const href = buildProductHref(review);
                            const st = statusMeta(normalizeStatus(review.status));

                            const cardContent = (
                                <article className={styles.card}>
                                    <div className={styles.mediaCol}>
                                        {review.productImage ? (
                                            <img className={styles.productImg} src={review.productImage} alt={review.productName || 'Товар'} loading="lazy" />
                                        ) : (
                                            <div className={styles.productImgStub} aria-hidden="true" />
                                        )}
                                    </div>

                                    <div className={styles.contentCol}>
                                        <div className={styles.headerCol}>
                                            <span className={`${styles.badge} ${styles[`badge_${st.tone}`]}`}>{st.text}</span>
                                            <div className={styles.productName} title={review.productName || 'Товар'}>
                                                {review.productName || 'Товар'}
                                            </div>
                                        </div>

                                        <div className={styles.metaRow}>
                                            <ReviewStars value={review.rating} />
                                            <time className={styles.date} dateTime={review.createdAt}>
                                                {fmtDateRu(review.createdAt)}
                                            </time>
                                        </div>

                                        <div className={styles.text}>{review.text}</div>
                                    </div>
                                </article>
                            );

                            return (
                                <div key={review.id} className={styles.cardItem} role="listitem">
                                    {href ? (
                                        <a
                                            className={styles.cardLink}
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Открыть товар и перейти к блоку отзывов"
                                        >
                                            {cardContent}
                                        </a>
                                    ) : (
                                        <div className={`${styles.cardLink} ${styles.cardLinkDisabled}`}>{cardContent}</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {nextPage > 0 ? <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" /> : null}

                    {loadingMore ? (
                        <div className={styles.moreState} aria-live="polite">
                            Загружаем ещё отзывы…
                        </div>
                    ) : null}

                    {loadMoreError ? (
                        <div className={`${styles.moreState} ${styles.moreStateError}`} role="status">
                            {loadMoreError}
                        </div>
                    ) : null}
                </>
            )}

            <button type="button" className={styles.catalogBtn} onClick={() => navigate('/catalog')}>
                К покупкам
            </button>
        </div>
    );
};

export default ReviewsPanel;
